import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, Check, ShieldCheck, Building2, MailX } from "lucide-react";
import { isValidRut, formatRut } from "@/lib/rut";
import { track } from "@/lib/analytics";

const SPAM_LABEL = { 5: "AGRESIVO", 4: "ALTO", 3: "MEDIO", 2: "BAJO", 1: "MÍNIMO" };
const SPAM_COLOR = {
  5: "bg-red-100 text-red-800 border-red-300",
  4: "bg-orange-100 text-orange-800 border-orange-300",
  3: "bg-amber-100 text-amber-800 border-amber-300",
  2: "bg-lime-100 text-lime-800 border-lime-300",
  1: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function Onboarding() {
  const { refresh, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    nombre_completo: user?.nombre || "",
    rut: "",
    email_contacto: user?.email || "",
    telefono: "",
    direccion: "",
  });
  const [brokers, setBrokers] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedBrokers, setSelectedBrokers] = useState(new Set());
  const [selectedEmpresas, setSelectedEmpresas] = useState(new Set());
  const [consentEmpresas, setConsentEmpresas] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    (async () => {
      try {
        const [b, e] = await Promise.all([
          api.get("/brokers?entity_type=broker"),
          api.get("/brokers?entity_type=empresa"),
        ]);
        const brokersList = (b.data || []).filter((x) => (x.tier || 99) <= 2);
        setBrokers(brokersList);
        setEmpresas(e.data || []);
        // brokers pre-selected by default (all)
        setSelectedBrokers(new Set(brokersList.map((x) => x.slug)));
      } catch (err) {
        toast.error("No se pudo cargar el catálogo. Intenta recargar la página.");
      }
    })();
  }, []);

  const empresasByCat = useMemo(() => {
    const m = {};
    empresas.forEach((e) => {
      const k = e.category || "Otros";
      (m[k] = m[k] || []).push(e);
    });
    // sort empresas by spam_score desc within each cat
    Object.values(m).forEach((arr) => arr.sort((a, b) => (b.spam_score || 0) - (a.spam_score || 0)));
    return m;
  }, [empresas]);

  const toggle = (setFn, set, slug) => {
    const next = new Set(set);
    next.has(slug) ? next.delete(slug) : next.add(slug);
    setFn(next);
  };

  const submitStep1 = (e) => {
    e.preventDefault();
    if (!isValidRut(form.rut)) {
      toast.error("RUT inválido. Verifica el dígito verificador.");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitStep2 = () => {
    if (selectedBrokers.size === 0) {
      toast.error("Debes mantener al menos un broker seleccionado.");
      return;
    }
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitOnboarding = async () => {
    if (selectedEmpresas.size > 0 && !consentEmpresas) {
      toast.error("Debes aceptar el consentimiento explícito para gestionar empresas.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/onboarding", {
        ...form,
        rut: formatRut(form.rut),
        selected_brokers: Array.from(selectedBrokers),
        selected_empresas: Array.from(selectedEmpresas),
        consent_empresas: consentEmpresas,
      });
      track("onboarding_completed_v2", {
        brokers: selectedBrokers.size,
        empresas: selectedEmpresas.size,
        consent: consentEmpresas,
      });
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitPlan = async () => {
    setLoading(true);
    track("checkout_started", { plan: "borrame", provider: "transbank" });
    try {
      const { data } = await api.post("/subscribe", { plan: "borrame" });
      if (data.mode === "transbank" && data.webpay_url && data.token) {
        try {
          sessionStorage.setItem("tbk_external_reference", data.external_reference);
        } catch (_e) {}
        track("checkout_redirect_webpay", { external_reference: data.external_reference });
        toast.success("Redirigiendo a Webpay Transbank…");
        // Webpay requires a POST form submit with token_ws (not a GET redirect).
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.webpay_url;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "token_ws";
        input.value = data.token;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
        return;
      }
      // mock fallback
      await api.post("/requests/dispatch");
      track("arcop_dispatched", { source: "onboarding_mock" });
      await refresh();
      toast.success("Solicitudes ARCOP en marcha");
      navigate("/dashboard");
    } catch (err) {
      track("checkout_error", { reason: String(err.response?.status || "unknown") });
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F9F9F8] min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        <button
          onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}
          className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-8"
          data-testid="ob-back"
        >
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>

        <StepsHeader step={step} />

        {step === 1 && (
          <>
            <div className="eyebrow mb-3 text-red-600">Paso 1 / 4 · Datos del titular</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              Cuéntanos quién eres.
            </h1>
            <p className="mt-3 text-gray-600 max-w-2xl">
              Usamos estos datos únicamente para personalizar y firmar cada solicitud ARCOP.
              No los cedemos a nadie más.
            </p>

            <form onSubmit={submitStep1} className="mt-10 space-y-5 bg-white border border-gray-300 p-6 sm:p-8" data-testid="onboarding-form">
              <Field label="Nombre completo" testid="ob-nombre" value={form.nombre_completo} onChange={update("nombre_completo")} />
              <Field label="RUT" testid="ob-rut" placeholder="12.345.678-9" mono value={form.rut} onChange={update("rut")} />
              <Field label="Email de contacto" testid="ob-email" type="email" value={form.email_contacto} onChange={update("email_contacto")} />
              <Field label="Teléfono" testid="ob-tel" value={form.telefono} onChange={update("telefono")} />
              <Field label="Dirección actual" testid="ob-dir" value={form.direccion} onChange={update("direccion")} />
              <button data-testid="ob-submit" className="w-full bg-[#0B2447] hover:bg-[#071932] text-white font-semibold py-3 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5">
                Continuar <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}

        {step === 2 && (
          <BrokersStep
            brokers={brokers}
            selected={selectedBrokers}
            onToggle={(slug) => toggle(setSelectedBrokers, selectedBrokers, slug)}
            onAll={() => setSelectedBrokers(new Set(brokers.map((x) => x.slug)))}
            onNone={() => setSelectedBrokers(new Set())}
            onNext={submitStep2}
          />
        )}

        {step === 3 && (
          <EmpresasStep
            empresasByCat={empresasByCat}
            selected={selectedEmpresas}
            onToggle={(slug) => toggle(setSelectedEmpresas, selectedEmpresas, slug)}
            consent={consentEmpresas}
            setConsent={setConsentEmpresas}
            onSkip={() => {
              // allow to proceed without empresas
              setSelectedEmpresas(new Set());
              submitOnboarding();
            }}
            onConfirm={submitOnboarding}
            loading={loading}
          />
        )}

        {step === 4 && (
          <SubscribeStep
            loading={loading}
            onPay={submitPlan}
            brokersCount={selectedBrokers.size}
            empresasCount={selectedEmpresas.size}
          />
        )}
      </div>
    </div>
  );
}

function StepsHeader({ step }) {
  const labels = ["Datos", "Brokers", "Empresas", "Plan"];
  return (
    <div className="flex items-center gap-2 mb-10 flex-wrap" data-testid="wizard-steps">
      {labels.map((label, i) => {
        const n = i + 1;
        return (
          <div key={n} className="flex items-center gap-2">
            <div className={`w-7 h-7 flex items-center justify-center text-xs font-bold ${step >= n ? "bg-[#0B2447] text-white" : "bg-gray-200 text-gray-500"}`}>
              {step > n ? "✓" : n}
            </div>
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-gray-700 hidden sm:inline">{label}</span>
            {n < labels.length && <div className="w-6 sm:w-10 h-px bg-gray-300 mx-2" />}
          </div>
        );
      })}
    </div>
  );
}

function BrokersStep({ brokers, selected, onToggle, onAll, onNone, onNext }) {
  return (
    <>
      <div className="eyebrow mb-3 text-red-600">Paso 2 / 4 · Brokers</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
        Los brokers están pre-seleccionados.
      </h1>
      <p className="mt-3 text-gray-600 max-w-2xl">
        Estos son los rutificadores y brokers de datos donde encontramos (o presumimos) evidencia real
        de que aparece tu RUT. Los enviaremos como <b>solicitud de supresión total</b> bajo el
        art. 12 letra d de la Ley 21.719. Puedes desmarcar los que no quieras.
      </p>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-mono text-gray-700" data-testid="brokers-count">
          {selected.size} de {brokers.length} seleccionados
        </div>
        <div className="flex gap-2">
          <button onClick={onAll} data-testid="brokers-all" className="text-xs uppercase tracking-[0.12em] font-bold text-[#0B2447] hover:underline">Todos</button>
          <span className="text-gray-300">|</span>
          <button onClick={onNone} data-testid="brokers-none" className="text-xs uppercase tracking-[0.12em] font-bold text-gray-500 hover:underline">Ninguno</button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="brokers-grid">
        {brokers.map((b) => {
          const on = selected.has(b.slug);
          return (
            <button
              key={b.slug}
              onClick={() => onToggle(b.slug)}
              data-testid={`broker-card-${b.slug}`}
              className={`text-left p-4 border transition-all ${on ? "border-[#0B2447] bg-[#0B2447] text-white" : "border-gray-300 bg-white text-gray-900 hover:border-[#0B2447]"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm">{b.name}</div>
                  <div className={`mt-1 text-xs ${on ? "text-white/70" : "text-gray-500"}`}>
                    Tier {b.tier} · {(b.data_types || []).join(", ")}
                  </div>
                </div>
                <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center ${on ? "border-white bg-white" : "border-gray-300"}`}>
                  {on && <Check className="w-3 h-3 text-[#0B2447]" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={onNext}
        data-testid="brokers-next"
        disabled={selected.size === 0}
        className="mt-8 w-full bg-[#0B2447] hover:bg-[#071932] text-white font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-60"
      >
        Continuar a Empresas <ArrowRight className="w-4 h-4" />
      </button>
    </>
  );
}

function EmpresasStep({ empresasByCat, selected, onToggle, consent, setConsent, onSkip, onConfirm, loading }) {
  const categories = Object.keys(empresasByCat).sort();
  return (
    <>
      <div className="eyebrow mb-3 text-red-600">Paso 3 / 4 · Empresas</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
        ¿A quién le entregaste tus datos alguna vez?
      </h1>
      <p className="mt-3 text-gray-600 max-w-2xl">
        Bancos, telcos, retail: probablemente tienen tu RUT desde hace años y te bombardean con SPAM.
        Selecciona los que reconoces y les enviaremos una <b>solicitud de oposición al marketing +
        supresión de lo no obligatorio</b> (art. 12 letras d y e Ley 21.719). Reconocemos que tienen
        obligaciones legales de retención — no exigimos borrado total.
      </p>

      <div className="mt-4 flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 p-3 text-blue-900" data-testid="empresas-info">
        <MailX className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Estas empresas <b>no se escanean</b>. Se basan en tu declaración. No aparecerán marcadas como “EXPUESTO”.</span>
      </div>

      <div className="mt-6 text-sm font-mono text-gray-700" data-testid="empresas-count">
        {selected.size} empresas seleccionadas
      </div>

      <div className="mt-2 space-y-6" data-testid="empresas-grid">
        {categories.map((cat) => (
          <div key={cat}>
            <div className="eyebrow text-gray-500 mb-2 flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" /> {cat}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {empresasByCat[cat].map((e) => {
                const on = selected.has(e.slug);
                const score = e.spam_score || 3;
                return (
                  <button
                    key={e.slug}
                    onClick={() => onToggle(e.slug)}
                    data-testid={`empresa-card-${e.slug}`}
                    className={`text-left p-4 border transition-all ${on ? "border-[#0B2447] bg-[#0B2447] text-white" : "border-gray-300 bg-white text-gray-900 hover:border-[#0B2447]"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{e.name}</div>
                        <div className={`mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-wider ${on ? "border-white/60 bg-white/10 text-white" : SPAM_COLOR[score]}`}>
                          SPAM {SPAM_LABEL[score]}
                        </div>
                        <div className={`mt-2 text-[11px] leading-snug ${on ? "text-white/70" : "text-gray-500"}`}>
                          {e.retention_note}
                        </div>
                      </div>
                      <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center ${on ? "border-white bg-white" : "border-gray-300"}`}>
                        {on && <Check className="w-3 h-3 text-[#0B2447]" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {selected.size > 0 && (
        <label className="mt-8 flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 cursor-pointer" data-testid="empresas-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#0B2447]"
            data-testid="empresas-consent-checkbox"
          />
          <span className="text-sm text-gray-800 leading-relaxed">
            <b>Consentimiento explícito (art. 12 Ley 21.719):</b> Declaro que tengo o he tenido relación
            comercial con las empresas seleccionadas y autorizo a borra-me.com a ejercer, en mi
            nombre, los derechos de oposición al marketing y supresión parcial de datos
            (no sujetos a obligación legal de retención) ante cada una de ellas.
          </span>
        </label>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button
          onClick={onSkip}
          data-testid="empresas-skip"
          className="flex-1 border border-gray-400 text-gray-700 font-semibold py-3 hover:bg-gray-100"
        >
          Saltar y continuar solo con brokers
        </button>
        <button
          onClick={onConfirm}
          disabled={loading || (selected.size > 0 && !consent)}
          data-testid="empresas-confirm"
          className="flex-1 bg-[#0B2447] hover:bg-[#071932] text-white font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          Confirmar selecciones <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}

function SubscribeStep({ loading, onPay, brokersCount, empresasCount }) {
  return (
    <>
      <div className="eyebrow mb-3 text-red-600">Paso 4 / 4 · Pago único</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
        Activa tu limpieza completa.
      </h1>
      <p className="mt-3 text-gray-600 max-w-2xl">
        Un solo pago. Sin permanencia. Sin renovaciones. Al confirmar, generamos:
        <br />
        <span className="font-semibold text-gray-900">
          {brokersCount} solicitudes ARCOP (brokers) + {empresasCount} de oposición al marketing (empresas).
        </span>
      </p>

      <div className="mt-6 bg-[#0F172A] text-white p-8" data-testid="plan-card">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-400">borra-me.com · pago único</div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-5xl font-bold tracking-tight">$39.990</span>
          <span className="text-gray-400">CLP · una sola vez</span>
        </div>
        <ul className="mt-7 space-y-2.5 text-sm text-gray-100">
          {[
            "Escaneo real de brokers y rutificadores chilenos",
            "Solicitudes ARCOP formales a cada broker seleccionado",
            "Detener spam de bancos, telcos y retail (empresas)",
            "Escalada automática a APDP si no responden en 30 días (art. 15 Ley 21.719)",
            "Re-escaneo automático cada 45 días — vigilancia vitalicia",
            "Generación de borrador de denuncia incluida",
            "5 escaneos gratuitos adicionales para otros RUT (familia, socios)",
          ].map((f) => (
            <li key={f} className="flex items-start gap-3">
              <span className="text-[#DC2626] flex-shrink-0 mt-1">✓</span>
              <span className="leading-relaxed">{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 bg-amber-50 border border-amber-200 p-5 text-xs text-gray-800 leading-relaxed flex gap-3">
        <ShieldCheck className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <span>
          <b>Marco legal:</b> Ley 21.719 (2024) sobre Protección y Tratamiento de Datos Personales de
          Chile. borra-me.com actúa como apoderado técnico del titular, ejerciendo los derechos ARCOP
          (art. 12) con base legal específica por cada responsable, con vigilancia vitalicia sobre
          este RUT.
        </span>
      </div>

      <button
        onClick={onPay}
        data-testid="confirm-subscribe"
        disabled={loading}
        className="mt-8 w-full bg-[#0B2447] hover:bg-[#071932] text-white font-semibold py-3 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      >
        {loading ? "Redirigiendo a Webpay…" : "Pagar $39.990 con Webpay Transbank"} <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-xs text-gray-500 text-center mt-3">
        Pago procesado por Webpay Transbank · Tarjeta débito/crédito/prepago · Sin permanencia
      </p>
    </>
  );
}

function Field({ label, testid, mono, ...rest }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <input required data-testid={testid} className={`w-full px-4 py-3 border border-gray-300 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent ${mono ? "font-mono" : ""}`} {...rest} />
    </div>
  );
}
